import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download } from "lucide-react";
import { Seo } from "@/components/Seo";
import {
    StatCard,
    LeadsTable,
    LeadsEmptyState,
    LeadDetailPanel,
} from "@/components/admin/parts";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
            setLeads(l.data);
            setStats(s.data);
        } catch {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const changeStatus = async (id, status) => {
        try {
            await api.patch(`/leads/${id}/status`, { status });
            toast.success("Status updated");
            load();
            setSelected((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
        } catch {
            toast.error("Update failed");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/leads/${id}`);
            toast.success("Lead deleted");
            setSelected(null);
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const exportCsv = () => {
        if (!leads.length) return;
        const headers = ["Practice", "Contact", "Email", "Phone", "Providers", "PM System", "Status", "Message", "Received"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const rows = leads.map((l) => [
            l.practice_name, l.contact_name, l.email, l.phone,
            l.num_providers, l.pm_system, l.status, l.message, l.created_at,
        ]);
        const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dentassists-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}`);
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
            <Seo title="Leads Dashboard — DentAssists" description="Internal DentAssists leads dashboard." path="/admin" noindex />
            <header className="border-b border-linec bg-cream sticky top-0 z-30">
                <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="DentAssists logo" className="w-9 h-9 rounded-full" />
                        <div>
                            <div className="font-heading text-lg text-ink leading-none">DentAssists</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft mt-1">
                                Leads Dashboard
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:block text-sm text-inksoft">{user?.email}</span>
                        <button
                            onClick={exportCsv}
                            disabled={!leads.length}
                            data-testid="export-csv-button"
                            className="inline-flex items-center gap-2 text-sm border border-linec rounded-sm px-4 py-2 text-ink hover:bg-stone2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Download size={15} /> <span className="hidden sm:inline">Export CSV</span>
                        </button>
                        <button
                            onClick={load}
                            data-testid="refresh-button"
                            className="p-2 border border-linec rounded-sm text-inksoft hover:bg-stone2 transition-colors"
                            aria-label="Refresh"
                        >
                            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        </button>
                        <button
                            onClick={logout}
                            data-testid="logout-button"
                            className="inline-flex items-center gap-2 text-sm border border-linec rounded-sm px-4 py-2 text-ink hover:bg-stone2 transition-colors"
                        >
                            <LogOut size={15} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1240px] mx-auto px-6 lg:px-10 py-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard label="Total leads" value={stats.total} accent />
                    <StatCard label="New" value={stats.new} />
                    <StatCard label="Contacted" value={stats.contacted} />
                    <StatCard label="Closed" value={stats.closed} />
                </div>

                {leads.length === 0 && !loading ? (
                    <LeadsEmptyState />
                ) : (
                    <LeadsTable leads={leads} onSelect={setSelected} />
                )}
            </main>

            {selected && (
                <LeadDetailPanel
                    lead={selected}
                    onClose={() => setSelected(null)}
                    onChangeStatus={changeStatus}
                    onDelete={remove}
                />
            )}

            <div className="text-center py-6">
                <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft hover:text-sage">
                    ← View public site
                </Link>
            </div>
        </div>
    );
}
